import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button, Table, Badge, InputGroup, Form, Spinner, Modal } from 'react-bootstrap';
import { FaSearch, FaPlus, FaEdit, FaCheck, FaTimes, FaUsers, FaEye, FaEyeSlash, FaUpload } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/apiClient';
import { toast } from 'react-toastify';
import OrgBulkUploadUsersModal from '../../admin/pages/Organizations/OrgBulkUploadUsersModal';

const ManageUsers = () => {
  const { user } = useAuth();
  const orgId = user?.organization_id;
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    status: 1,
    group_ids: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const orgGroups = useMemo(
    () => groups.filter((group) => group.organization_id && parseInt(group.organization_id, 10) === parseInt(orgId, 10)),
    [groups, orgId]
  );

  const parseGroupIds = (value) => {
    let raw = value;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id));
  };

  const getUserGroupNames = (record) => {
    const groupIds = parseGroupIds(record.group_ids);
    if (groupIds.length === 0) return [];
    return groupIds
      .map((id) => groups.find((group) => parseInt(group.id, 10) === id)?.name)
      .filter(Boolean);
  };

  const fetchUsers = async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const response = await api.organizations.getUsers(orgId);
      const list = response?.data?.data || response?.data || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.groups.list();
      setGroups(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchLicenseInfo = async () => {
    if (!orgId) return;
    try {
      const response = await api.licenses.getOrganizationUsage(orgId);
      setLicenseInfo(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching license info:', error);
    }
  };

  useEffect(() => {
    if (orgId) {
      fetchUsers();
      fetchGroups();
      fetchLicenseInfo();
    }
  }, [orgId]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleGroupToggle = (groupId) => {
    const id = groupId.toString();
    setFormData((prev) => {
      const exists = prev.group_ids.includes(id);
      return {
        ...prev,
        group_ids: exists
          ? prev.group_ids.filter((value) => value !== id)
          : [...prev.group_ids, id]
      };
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!editingUser && !formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (!editingUser && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddUser = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!orgId) {
      toast.error('Organization ID not found');
      return;
    }

    if (!editingUser) {
      try {
        const licenseCheck = await api.licenses.checkAvailability(orgId);
        if (!licenseCheck.data.success || !licenseCheck.data.data.available) {
          toast.error(licenseCheck.data.data.message || 'No licenses available for new users. Please request more licenses.');
          return;
        }
      } catch (error) {
        console.error('Error checking license availability:', error);
        toast.error('Failed to check license availability. Please try again.');
        return;
      }
    }

    setModalLoading(true);

    try {
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: 1,
        status: parseInt(formData.status, 10),
        group_ids: formData.group_ids.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id)),
        ...(formData.password && { password: formData.password })
      };

      if (editingUser) {
        await api.organizations.updateUser(orgId, editingUser.id, userData);
        toast.success('Learner updated successfully!');
      } else {
        await api.organizations.addUser(orgId, userData);
        try {
          await api.licenses.consumeLicense(orgId);
          toast.success('Learner added successfully! License consumed.');
        } catch (licenseError) {
          console.warn('User created but failed to consume license:', licenseError);
          toast.warning('Learner added successfully, but failed to update license count.');
        }
      }

      handleCloseModal();
      fetchUsers();
      fetchLicenseInfo();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Failed to save user: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const handleShowAddModal = () => {
    fetchLicenseInfo();
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      status: 1,
      group_ids: []
    });
    setFormErrors({});
    setShowPassword(false);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      status: 1,
      group_ids: []
    });
    setEditingUser(null);
    setFormErrors({});
    setShowPassword(false);
  };

  const handleOpenBulkUpload = () => {
    if (licenseInfo && licenseInfo.remaining_licenses <= 0) {
      toast.error('No licenses available. Please request more licenses before bulk uploading learners.');
      return;
    }
    setShowBulkUploadModal(true);
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      if (orgId) {
        await api.organizations.updateUser(orgId, userId, {
          status: currentStatus === 1 ? 0 : 1
        });
        toast.success('User status updated successfully!');
        fetchUsers();
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter((record) => {
    const matchesSearch =
      record.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.email?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    switch (filterStatus) {
      case 'active':
        matchesStatus = record.status === 1 && !record.deleted;
        break;
      case 'inactive':
        matchesStatus = record.status === 0 && !record.deleted;
        break;
      default:
        matchesStatus = !record.deleted;
        break;
    }

    return matchesSearch && matchesStatus;
  }) : [];

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheck className="w-4 h-4" />
            Active
          </span>
        );
      case 0:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <FaTimes className="w-4 h-4" />
            Inactive
          </span>
        );
      default:
        return <Badge bg="warning">Unknown</Badge>;
    }
  };

  const getRoleBadge = () => <Badge bg="primary">Learner</Badge>;

  const licensesAvailable = !licenseInfo || licenseInfo.remaining_licenses > 0;

  return (
    <div className="container-fluid p-0 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="p-3 sm:p-4 md:p-5">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Manage Users
          </h5>
        </div>

        <Row className="mb-3 g-2 sm:g-3">
          <Col xs={12} md={5} className="mb-2 mb-md-0">
            <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
              <InputGroup.Text className="bg-white border-0 text-purple-600 px-3 sm:px-3">
                <FaSearch className="w-4 h-4" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearch}
                className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
              />
            </InputGroup>
          </Col>
          <Col xs={12} sm={6} md={2} className="mb-2 mb-md-0">
            <Form.Select
              value={filterStatus}
              onChange={handleFilterChange}
              className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col xs={12} sm={6} md={2}>
            <div className="d-grid w-100 h-100">
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleShowAddModal}
                disabled={!licensesAvailable}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2"
                style={{ width: '100%' }}
                title={!licensesAvailable ? 'No licenses available' : 'Add learner'}
              >
                <FaPlus className="w-4 h-4" />
                <span className="whitespace-nowrap">Add Learner</span>
              </Button>
            </div>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <div className="d-grid w-100 h-100">
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleOpenBulkUpload}
                disabled={!licensesAvailable}
                className="border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2"
                style={{ width: '100%' }}
                title={!licensesAvailable ? 'No licenses available' : 'Bulk upload learners'}
              >
                <FaUpload className="w-4 h-4" />
                <span className="whitespace-nowrap">Bulk Upload</span>
              </Button>
            </div>
          </Col>
        </Row>

        {licenseInfo && (
          <div className="mb-3 p-3 bg-white rounded-xl border border-purple-100 shadow-sm">
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div>
                <span className="text-purple-600 block">Total Licenses</span>
                <strong className="text-purple-800">{licenseInfo.total_licenses}</strong>
              </div>
              <div>
                <span className="text-orange-600 block">Used</span>
                <strong className="text-orange-700">{licenseInfo.used_licenses}</strong>
              </div>
              <div>
                <span className="text-emerald-600 block">Available</span>
                <strong className={licenseInfo.remaining_licenses > 0 ? 'text-emerald-700' : 'text-red-600'}>
                  {licenseInfo.remaining_licenses}
                </strong>
              </div>
            </div>
          </div>
        )}

        <Card className="border border-purple-100 bg-white shadow-lg rounded-xl sm:rounded-2xl overflow-hidden">
          <Card.Body className="p-0">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" role="status" className="text-purple-600">
                  <span className="visually-hidden">Loading users...</span>
                </Spinner>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-6 sm:py-8 bg-white/80 backdrop-blur shadow-md border border-purple-100 rounded-xl sm:rounded-2xl">
                <FaUsers className="text-purple-400 mb-3" style={{ fontSize: '2rem' }} />
                <p className="text-gray-600 mb-0 text-sm sm:text-base">
                  {searchTerm ? 'No learners found matching your search.' : 'No learners in your organization yet.'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="m-0 min-w-max text-xs sm:text-sm">
                  <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                    <tr>
                      <th className="py-2 px-3">User</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3">Role</th>
                      <th className="py-2 px-3">Groups</th>
                      <th className="py-2 px-3">Joined</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((record) => {
                      const initials = (record.first_name && record.last_name)
                        ? `${record.first_name.charAt(0)}${record.last_name.charAt(0)}`.toUpperCase()
                        : record.email?.charAt(0).toUpperCase() || 'U';
                      const groupNames = getUserGroupNames(record);

                      return (
                        <tr key={record.id} className="border-b border-purple-50 hover:bg-purple-50/50 transition-all">
                          <td className="py-2 px-3 align-middle">
                            <div className="d-flex align-items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 d-flex align-items-center justify-content-center text-white fw-bold shadow-sm text-xs">
                                {initials}
                              </div>
                              <div className="fw-semibold text-gray-800 text-sm">
                                {record.first_name} {record.last_name}
                              </div>
                            </div>
                          </td>
                          <td className="py-2 px-3 align-middle text-gray-600 text-sm">{record.email}</td>
                          <td className="py-2 px-3 align-middle">{getRoleBadge(record.role)}</td>
                          <td className="py-2 px-3 align-middle">
                            {groupNames.length === 0 ? (
                              <span className="text-gray-400 text-xs">—</span>
                            ) : (
                              <div className="d-flex flex-wrap gap-1">
                                {groupNames.map((name) => (
                                  <Badge key={name} bg="info" className="px-2 py-1 text-xs">{name}</Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 align-middle text-gray-600 text-xs">
                            {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="py-2 px-3 align-middle">{getStatusBadge(record.status)}</td>
                          <td className="py-2 px-3 align-middle">
                            <div className="d-flex gap-2">
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => {
                                  setEditingUser(record);
                                  setFormData({
                                    first_name: record.first_name || '',
                                    last_name: record.last_name || '',
                                    email: record.email || '',
                                    password: '',
                                    status: record.status ?? 1,
                                    group_ids: parseGroupIds(record.group_ids).map((id) => id.toString())
                                  });
                                  setFormErrors({});
                                  setShowAddModal(true);
                                }}
                                className="border-0 border-purple-300 text-purple-600 rounded hover:bg-purple-50 transition p-1"
                                title="Edit learner"
                              >
                                <FaEdit className="w-5 h-5" />
                              </Button>
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleStatusToggle(record.id, record.status)}
                                className={`border-0 rounded transition p-1 ${record.status === 1 ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                                title={record.status === 1 ? 'Deactivate learner' : 'Activate learner'}
                              >
                                {record.status === 1 ? <FaTimes className="w-5 h-5" /> : <FaCheck className="w-5 h-5" />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={showAddModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
            <Modal.Title className="font-bold text-xl text-white">
              {editingUser ? 'Edit Learner' : 'Add New Learner'}
            </Modal.Title>
          </Modal.Header>
          {licenseInfo && !editingUser && (
            <div className="bg-purple-50 border-b border-purple-200 p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <small className="text-purple-600">Total Licenses</small>
                  <div className="text-xl font-bold text-purple-700">{licenseInfo.total_licenses}</div>
                </div>
                <div>
                  <small className="text-purple-600">Used</small>
                  <div className="text-xl font-bold text-orange-600">{licenseInfo.used_licenses}</div>
                </div>
                <div>
                  <small className="text-purple-600">Available</small>
                  <div className={`text-xl font-bold ${licenseInfo.remaining_licenses > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {licenseInfo.remaining_licenses}
                  </div>
                </div>
              </div>
              {licenseInfo.remaining_licenses === 0 && (
                <div className="mt-3 p-3 bg-red-100 text-red-700 rounded-lg text-sm text-center">
                  No licenses remaining. Please request more licenses before creating learners.
                </div>
              )}
            </div>
          )}
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <Form onSubmit={handleAddUser}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.first_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    placeholder="Enter first name"
                  />
                  {formErrors.first_name && <p className="text-red-500 text-xs mt-1">{formErrors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.last_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    placeholder="Enter last name"
                  />
                  {formErrors.last_name && <p className="text-red-500 text-xs mt-1">{formErrors.last_name}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={Boolean(editingUser)}
                  readOnly={Boolean(editingUser)}
                  className={`w-full px-3 py-2 rounded-lg border ${formErrors.email ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm ${editingUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter email address"
                />
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                {editingUser && <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation</p>}
              </div>

              {!editingUser && (
                <div className="mb-4 relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 rounded-lg border ${formErrors.password ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm pr-10`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                <input
                  type="text"
                  value="Learner"
                  readOnly
                  disabled
                  className="w-full px-3 py-2 rounded-lg border border-purple-200 bg-gray-100 text-sm text-gray-700 cursor-not-allowed"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Organization Groups <span className="text-gray-500 text-xs font-normal">(optional)</span>
                </label>
                <div className="border border-purple-200 rounded-lg p-3 bg-white max-h-48 overflow-y-auto">
                  {orgGroups.length === 0 ? (
                    <p className="text-muted text-sm mb-0">No groups linked to this organization.</p>
                  ) : (
                    orgGroups.map((group) => {
                      const groupId = group.id.toString();
                      return (
                        <Form.Check
                          key={group.id}
                          type="checkbox"
                          id={`org-learner-group-${groupId}`}
                          label={group.name}
                          checked={formData.group_ids.includes(groupId)}
                          onChange={() => handleGroupToggle(groupId)}
                          className="mb-2"
                        />
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Only organization groups are listed.</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
                <Row className="w-100 g-3">
                  <Col md={6}>
                    <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                      Cancel
                    </Button>
                  </Col>
                  <Col md={6}>
                    <Button
                      bsPrefix="custom-hamburger"
                      type="submit"
                      disabled={modalLoading || (!editingUser && !licensesAvailable)}
                      className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                    >
                      {modalLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {editingUser ? 'Updating...' : 'Adding...'}
                        </>
                      ) : (
                        editingUser ? 'Update Learner' : 'Add Learner'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Modal.Footer>
            </Form>
          </Modal.Body>
        </Modal>

        <OrgBulkUploadUsersModal
          show={showBulkUploadModal}
          onHide={() => setShowBulkUploadModal(false)}
          orgId={orgId}
          onImported={async () => {
            await fetchUsers();
            await fetchLicenseInfo();
          }}
        />
      </div>
    </div>
  );
};

export default ManageUsers;
